/**
 * Where a newcomer came from and how long they stayed, in coarse buckets, for
 * the funnel. Nothing here identifies anyone: a short name from a fixed list
 * for the site that sent them, the app whose built-in browser they are in,
 * and rounded times. Never a full address, a query string or a user agent.
 */

export type Via =
  | 'tiktok'
  | 'instagram'
  | 'facebook'
  | 'x'
  | 'telegram'
  | 'whatsapp'
  | 'reddit'
  | 'discord'
  | 'bitcointalk'
  | 'hn'
  | 'luma'
  | 'talk'
  | 'directory'
  | 'site'
  | 'search'
  | 'other'
  | 'direct';

const VIAS: readonly Via[] = [
  'tiktok',
  'instagram',
  'facebook',
  'x',
  'telegram',
  'whatsapp',
  'reddit',
  'discord',
  'bitcointalk',
  'hn',
  'luma',
  // A flyer or a QR code at a talk, and a listing in a directory.
  'talk',
  'directory',
  'site',
  'search',
  'other',
  'direct',
];

/** A `?via=` we put on our own links wins; then the site that sent them; else direct. */
export function viaOf(search: string, referrer: string): Via {
  const asked =
    new URLSearchParams(search).get('via') ?? new URLSearchParams(search).get('utm_source');
  if (asked !== null) {
    const v = asked.toLowerCase() === 'twitter' ? 'x' : asked.toLowerCase();
    return (VIAS as readonly string[]).includes(v) ? (v as Via) : 'other';
  }
  let host = '';
  try {
    host = referrer === '' ? '' : new URL(referrer).hostname.toLowerCase();
  } catch {
    return 'other';
  }
  if (host === '') return 'direct';
  const is = (...names: string[]): boolean =>
    names.some((n) => host === n || host.endsWith(`.${n}`));
  if (is('tiktok.com')) return 'tiktok';
  if (is('instagram.com')) return 'instagram';
  if (is('facebook.com', 'fb.com', 'fb.me')) return 'facebook';
  if (is('t.co', 'x.com', 'twitter.com')) return 'x';
  if (is('t.me', 'telegram.org', 'telegram.me')) return 'telegram';
  if (is('whatsapp.com', 'wa.me')) return 'whatsapp';
  if (is('reddit.com', 'redd.it')) return 'reddit';
  if (is('discord.com', 'discord.gg', 'discordapp.com')) return 'discord';
  if (is('bitcointalk.org')) return 'bitcointalk';
  if (is('news.ycombinator.com')) return 'hn';
  if (is('lu.ma', 'luma.com')) return 'luma';
  if (is('xchangehq.com')) return 'site';
  if (/(^|\.)(google|bing|duckduckgo|yahoo|yandex)\./.test(host)) return 'search';
  return 'other';
}

/** The app whose built-in browser this is, if any; their storage can be cleared without asking. */
export function inAppOf(userAgent: string): 'tiktok' | 'instagram' | 'facebook' | 'x' | 'none' {
  if (/musical_ly|BytedanceWebview|TikTok/i.test(userAgent)) return 'tiktok';
  if (/Instagram/i.test(userAgent)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/.test(userAgent)) return 'facebook';
  if (/Twitter/i.test(userAgent)) return 'x';
  return 'none';
}

/** How long the page took to show the welcome, rounded so it says only "fast" or "slow". */
export function loadBucket(ms: number): string {
  if (ms < 1000) return 'under 1s';
  if (ms < 3000) return '1-3s';
  if (ms < 6000) return '3-6s';
  if (ms < 10_000) return '6-10s';
  return '10s+';
}

/** How long someone looked at the welcome before leaving it. */
export function stayBucket(ms: number): string {
  if (ms < 5000) return 'under 5s';
  if (ms < 15_000) return '5-15s';
  if (ms < 60_000) return '15-60s';
  return '60s+';
}
